// ============================================================================
// Time & Labor — Antonela's console. THE LANDING PAGE IS THE EXCEPTION QUEUE
// (spec §5 Part 3): not a menu, not an employee list — only what is anomalous,
// grouped by severity, 24-hour clear target. Auto-handled rows stay visible.
//
// Reviewing marks the exception with who/when/why; correcting a shift goes
// through the Timecards drawer (rpc_time_correct_segment — reason mandatory).
// ============================================================================

import { canReviewTime, hasFullAmAuthority, isAmRole, type RoleLike } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, PageHeader, TabBar } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { fmtWhen, QueueGroup, QueueRow } from './console-parts';
import { formatSiteDateTime } from '../../lib/siteTime';
import { minutesToHours } from './time-vocabulary';
import {
  reviewException,
  staffResolveAutoclose,
  useAutocloseCoaching,
  useAutocloseResidual,
  useExceptionQueue,
} from './hooks/use-time-data';
import type { ExceptionRow, ExceptionSeverity } from './types';

export const TIME_TABS = [
  { id: 'queue', label: 'Exceptions' },
  { id: 'timecards', label: 'Timecards' },
  // ⛔ Its own tab, alongside the per-person list on the punch card — Ben
  // chose both (2026-09-22). One view, one component, two filters.
  { id: 'removed', label: 'Removed' },
  { id: 'punch', label: 'Punch' },
  { id: 'admin', label: 'Workers & caps' },
  { id: 'hours-links', label: 'Hours links' },
  { id: 'pay-run', label: 'Pay run' },
  { id: 'reports', label: 'Payroll & reports' },
] as const;
export type TimeTabId = (typeof TIME_TABS)[number]['id'];

// The account manager's own two tabs. They sit at the front of an account
// manager's bar; the HR console does not carry them.
const AM_OWN_TABS = [
  { id: 'hours', label: 'Hours' },
  { id: 'night-managers', label: 'My night managers' },
] as const;
export type TimeLaborTabId = TimeTabId | (typeof AM_OWN_TABS)[number]['id'];
type TabDef = { id: TimeLaborTabId; label: string };

const tabById = (id: TimeTabId): TabDef => TIME_TABS.find((t) => t.id === id) as TabDef;

/**
 * Which Time & Labor tabs a sign-in sees. Each tab is shown where the database
 * lets that seat act on it, never by name (approval is a position).
 *
 *  - An account manager: Hours · My night managers · Timecards · Hours links.
 *    Timecards is there to record a shift manually and correct one at their own
 *    buildings — rpc_time_manual_segment / rpc_time_correct_segment admit
 *    is_am_role() AND is_my_facility() (Ben, 2026-10-26, item #982).
 *  - The national AM (is_am_role() AND has_full_am_authority()): every tab the
 *    database lets that seat use (Ben approved #981's option 1, 2026-10-26).
 *    Pay run and Payroll & reports stay off: fn_time_export_gate() admits only
 *    HR, finance and executives.
 *  - Everyone else on the console: the eight HR tabs, unchanged.
 */
export function timeTabsFor(role: RoleLike): readonly TabDef[] {
  if (!isAmRole(role)) return TIME_TABS;
  if (!hasFullAmAuthority(role)) {
    return [...AM_OWN_TABS, tabById('timecards'), tabById('hours-links')];
  }
  return [
    ...AM_OWN_TABS,
    ...TIME_TABS.filter((t) => t.id !== 'pay-run' && t.id !== 'reports'),
  ];
}

export function useTimeTabsNav(active: TimeLaborTabId) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const role = profile?.role ?? 'none';
  // An account manager lands on Hours at /time, so the exception queue lives
  // at /time/exceptions for them; HR keeps it at /time.
  const amSide = isAmRole(role);
  return {
    tabs: timeTabsFor(role),
    activeId: active,
    onChange: (id: TimeLaborTabId) => {
      const path = id === 'hours' ? '/time'
        : id === 'night-managers' ? '/time/night-managers'
        : id === 'queue' ? (amSide ? '/time/exceptions' : '/time')
        : id === 'timecards' ? '/time/timecards'
        : id === 'removed' ? '/time/removed'
        : id === 'punch' ? '/time/punch'
        : id === 'pay-run' ? '/time/pay-run'
        : id === 'reports' ? '/time/reports'
        : id === 'hours-links' ? '/time/hours-links'
        : '/time/admin';
      navigate(path);
    },
  };
}

export function TimeConsole() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { rows, loading, error, reload } = useExceptionQueue();
  const residual = useAutocloseResidual();
  const coaching = useAutocloseCoaching();
  const tabsNav = useTimeTabsNav('queue');

  const [reviewing, setReviewing] = useState<ExceptionRow | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const role = profile?.role ?? 'none';
  const canReview = canReviewTime(role);
  // Correct mirrors rpc_time_correct_segment, which also admits the national
  // AM at any building or market (has_full_am_authority). Marking a flag
  // reviewed does not: segment_exceptions' hr_review rule is HR / executive.
  const canCorrect = canReview || hasFullAmAuthority(role);

  const open = useMemo(() => rows.filter((r) => r.status === 'open'), [rows]);
  const autoHandled = useMemo(
    () => rows.filter((r) => r.status === 'auto_resolved').slice(0, 20),
    [rows],
  );
  const bySeverity = (sev: ExceptionSeverity) => open.filter((r) => r.severity === sev);

  async function submitReview() {
    if (!reviewing) return;
    setBusy(true);
    setActionError(null);
    try {
      await reviewException(reviewing.exception_id, note.trim(), profile?.employee_id ?? null);
      setReviewing(null);
      setNote('');
      await reload();
    } catch (e) {
      setActionError(errorMessage(e, 'Could not mark this reviewed.'));
    } finally {
      setBusy(false);
    }
  }

  // The auto-close confirm path: HR stands by the FULL duration (it pays).
  // Changing the times instead goes through Correct — the RPC refuses any
  // other use (TCS_TIME_USE_CORRECTION), so the two doors cannot blur.
  const reviewingIsAutoclose =
    reviewing?.rule_code === 'daily_ceiling_auto_close' && reviewing?.segment_status === 'auto_closed';

  async function submitConfirmHours(exceptionId: string, confirmNote: string) {
    setBusy(true);
    setActionError(null);
    try {
      await staffResolveAutoclose(exceptionId, true, confirmNote.trim() || null);
      setReviewing(null);
      setNote('');
      await Promise.all([reload(), residual.reload(), coaching.reload()]);
    } catch (e) {
      setActionError(errorMessage(e, 'Could not confirm the hours.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow="Time & Labor"
        title="Exception queue"
        meta={
          loading
            ? 'Loading…'
            : `${open.length} open · ${bySeverity('high').length} high · target: clear within 24h. Nothing in this queue ever blocked a punch (D-03).`
        }
        actions={<Button size="sm" variant="secondary" onClick={() => void reload()}>Refresh</Button>}
      />
      <div className="mt-3"><TabBar {...tabsNav} /></div>

      {error && (
        <div className="mt-4 rounded-xl border border-st-danger-border bg-st-danger-bg px-4 py-3 text-sm text-st-danger-fg">
          {error}
        </div>
      )}

      <div className="mt-4 flex flex-col gap-4">
        {/* Escalation ceiling — a VIEW over open auto-closes, not a stored
            counter: 3+ unresolved, or 2+ in the open pay period. The output
            is a coaching conversation, never an automatic consequence. */}
        {coaching.rows.length > 0 && (
          <div className="rounded-xl border border-st-info-border bg-st-info-bg px-4 py-3">
            <div className="text-sm font-bold text-st-info-fg">Coaching — repeated unresolved auto-closes</div>
            <div className="mt-1 flex flex-col gap-1">
              {coaching.rows.map((c) => (
                <div key={c.worker_id} className="flex flex-wrap items-baseline gap-2 text-sm text-tcs-text">
                  <span className="font-semibold">{c.worker_name}</span>
                  <span className="text-tcs-text-secondary">
                    {c.open_autocloses} open auto-close{c.open_autocloses === 1 ? '' : 's'}
                    {c.open_this_period >= 2 ? ` · ${c.open_this_period} this pay period` : ''}
                    {' · latest '}{fmtWhen(c.latest_at)}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-1.5 text-xs text-tcs-text-muted">
              A pattern here is a conversation with the worker's supervisor — the shifts themselves still
              resolve one at a time below.
            </div>
          </div>
        )}

        {(['high', 'medium', 'low'] as const).map((sev) => {
          const group = bySeverity(sev);
          if (group.length === 0) return null;
          return (
            <QueueGroup
              key={sev}
              severity={sev}
              title={sev === 'high' ? 'High severity' : sev === 'medium' ? 'Medium severity' : 'Low severity'}
              count={group.length}
            >
              {group.map((r) => (
                <QueueRow
                  key={r.exception_id}
                  row={r}
                  canReview={canReview}
                  canCorrect={canCorrect}
                  onReview={(row) => { setReviewing(row); setNote(''); setActionError(null); }}
                  onCorrect={(segmentId) => navigate(`/time/timecards?correct=${segmentId}`)}
                />
              ))}
            </QueueGroup>
          );
        })}

        {!loading && open.length === 0 && (
          <div className="rounded-xl border border-st-ok-border bg-st-ok-bg px-4 py-8 text-center">
            <div className="text-base font-semibold text-st-ok-fg">Queue is clear</div>
            <div className="mt-1 text-sm text-tcs-text-secondary">
              Every punch is in and nothing is anomalous. New flags land here the moment a rule fires.
            </div>
          </div>
        )}

        {/* Self-service residual: auto-closes whose worker has NOT punched
            since the flag was raised — nobody has been asked yet. This is the
            follow-up list, and the pre-cutoff sweep before payroll: anything
            still here at close gets settled by HR, not left to ride. */}
        {residual.rows.length > 0 && (
          <QueueGroup severity="medium" title="Waiting on the worker's next punch — no prompt yet" count={residual.rows.length}>
            {residual.rows.map((r) => (
              <div key={r.exception_id} className="flex flex-wrap items-center gap-3 border-t border-tcs-border bg-tcs-raised px-4 py-3">
                <div className="min-w-[260px] flex-[2]">
                  <div className="font-semibold text-tcs-text">
                    {r.worker_name}
                    <span className="ml-2 font-normal text-tcs-text-secondary">{r.place_display}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-tcs-text-secondary">
                    Shift {formatSiteDateTime(r.started_at, r.site_tz)} → auto-closed {r.ended_at ? formatSiteDateTime(r.ended_at, r.site_tz) : '—'}
                    {r.raw_minutes != null && ` · ${minutesToHours(r.raw_minutes)} h on the record`}
                    {r.prompt_shown_count > 0
                      ? ` · prompted ${r.prompt_shown_count}× without an answer`
                      : ' · never prompted — they have not punched since'}
                  </div>
                </div>
                {canCorrect && (
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/time/timecards?correct=${r.segment_id}`)}
                      className="rounded-lg border border-tcs-border-strong bg-tcs-bg px-3 py-1.5 text-xs font-semibold text-tcs-text hover:bg-tcs-surface"
                    >
                      Correct…
                    </button>
                    {canReview && <button
                      type="button"
                      onClick={() => {
                        const match = rows.find((x) => x.exception_id === r.exception_id);
                        if (match) { setReviewing(match); setNote(''); setActionError(null); }
                      }}
                      className="rounded-lg bg-tcs-inverse px-3 py-1.5 text-xs font-semibold text-tcs-inverse-ink hover:bg-tcs-inverse-hover"
                    >
                      Settle…
                    </button>}
                  </div>
                )}
              </div>
            ))}
          </QueueGroup>
        )}

        {autoHandled.length > 0 && (
          <QueueGroup severity="low" title="Auto-handled — visible for audit" count={autoHandled.length}>
            {autoHandled.map((r) => (
              <QueueRow key={r.exception_id} row={r} canReview={false} onReview={() => {}} onCorrect={() => {}} />
            ))}
          </QueueGroup>
        )}
      </div>

      {/* Review drawer — a review is an answer, so it carries a note. */}
      {reviewing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim p-4 sm:items-center">
          <div className="w-full max-w-md rounded-2xl border border-tcs-border-strong bg-tcs-overlay shadow-xl">
            <div className="border-b border-tcs-border px-5 py-3 font-semibold text-tcs-text">
              Mark reviewed — {reviewing.worker_name}
            </div>
            <div className="flex flex-col gap-3 px-5 py-4">
              <div className="text-sm text-tcs-text-secondary">
                {reviewing.rule_label}{reviewing.detail ? ` — ${reviewing.detail}` : ''}
              </div>
              <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="review-note">
                What did you check? (optional)
              </label>
              <textarea
                id="review-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2 text-sm text-tcs-text"
                placeholder="e.g. Confirmed with the supervisor — hours are right."
              />
              {reviewingIsAutoclose && (
                <div className="rounded-lg border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg">
                  This shift was auto-closed and is still awaiting an answer.
                  <span className="font-semibold"> Confirm hours</span> stands by the full duration (it pays);
                  to change the times, use Correct on the Timecards tab instead.
                </div>
              )}
              {actionError && <div className="text-sm text-st-danger-fg">{actionError}</div>}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setReviewing(null)}>Cancel</Button>
                {reviewingIsAutoclose && (
                  <Button
                    size="sm"
                    loading={busy}
                    onClick={() => reviewing && void submitConfirmHours(reviewing.exception_id, note)}
                  >
                    Confirm hours (pays full)
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={reviewingIsAutoclose ? 'secondary' : undefined}
                  loading={busy}
                  onClick={() => void submitReview()}
                >
                  Mark reviewed
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
